import { computeMapOrigin } from '@/core';
import type { FeaturesQuery } from '@/graphql/graphql';
import { useState } from 'react';
import * as THREE from 'three';

const ZERO_ORIGIN = new THREE.Vector3(0, 0, 0);

/**
 * Floating origin for the 3D vector scene (float32 precision at projected
 * coordinates). Any point near the data will do, so it is picked once from
 * the first non-empty feature set and kept: recomputing it as layers stream
 * in walked every vertex (~100 ms on 300k) per consumer and, by moving,
 * rebuilt every feature's geometry on each merge. Re-picked only after all
 * features go away. The viewer must remount per project (key it by
 * `projectId`): otherwise it can go from one project's features straight to
 * the next's and keep the first project's origin. Compute it ONCE and pass it
 * to every consumer that must share the frame (GeoJson, the rainfall
 * overlays).
 */
export function useStableMapOrigin(features: FeaturesQuery['featuresByLayer'][number][] | undefined): THREE.Vector3 {
  const [origin, setOrigin] = useState<THREE.Vector3 | null>(() =>
    features?.length ? computeMapOrigin(features) : null,
  );
  // Derived state set during render — React re-renders before committing.
  if (!origin && features?.length) setOrigin(computeMapOrigin(features));
  else if (origin && !features?.length) setOrigin(null);
  return origin ?? ZERO_ORIGIN;
}
